import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import emptyKra from '../../../../img/empty.jpg';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSnackbar } from 'notistack';
import { deleteAssignedByUserType, getAssignedKra } from '../../../../redux/features/appraisalSlice';
import ImportData from '../ImportData';
import CustomSuspense from '../../../../components/Loader/CustomSuspense';
import Loader3D from '../../../../components/Loader/Loader3D';
const TagWill = lazy(() => import('./TagWill'));

const WillCard = ({ tag, handleDelete, setEditData, setExternalOpen }) => {
  const [showAll, setShowAll] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const libraryDetails = tag.libraryDetails || [];
  const limit = 3;
  const hasMore = libraryDetails.length > limit;
  const displayedDetails = showAll ? libraryDetails : libraryDetails.slice(0, limit);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          padding: 2.5,
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          border: '1px solid rgba(224, 224, 224, 0.4)',
          backgroundColor: '#ffffff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <Stack direction={'row'} spacing={1} mb={2} sx={{ alignItems: 'center' }}>
            <Chip
              icon={<Icon icon="tdesign:user-filled" width={16} height={16} />}
              label={tag.usertypeDetails?.userType || 'N/A'}
              color="secondary"
              variant="outlined"
              sx={{
                fontWeight: 'bold',
                borderRadius: '6px',
                paddingLeft: 0.5,
                borderColor: 'secondary.light',
              }}
            />
          </Stack>

          <Stack spacing={1} sx={{ width: '100%' }}>
            {displayedDetails.map((kra, index) => (
              <Stack
                key={index}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  padding: '8px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  border: '1px solid rgba(25, 118, 210, 0.08)',
                }}
              >
                <Chip
                  label={`${kra.weightage}%`}
                  color="primary"
                  size="small"
                  variant="filled"
                  icon={<Icon icon="fa-solid:percentage" width={12} height={12} />}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderRadius: '4px',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: 'text.primary',
                    wordBreak: 'break-word',
                  }}
                >
                  {kra.libraryName}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {hasMore && (
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={() => setShowAll(!showAll)}
              endIcon={<Icon icon={showAll ? 'fluent:chevron-up-12-filled' : 'fluent:chevron-down-12-filled'} />}
              sx={{
                mt: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.825rem',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              {showAll ? 'Show Less' : `Show All (${libraryDetails.length})`}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            flexDirection: 'row',
            gap: 1.5,
            mt: { xs: 2, sm: 0 },
            ml: { xs: 0, sm: 2 },
            alignSelf: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => {
              setEditData(tag);
              setExternalOpen(true);
            }}
            sx={{
              minWidth: '40px',
              height: '40px',
              borderRadius: '8px',
              borderColor: 'primary.light',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            <Icon icon="mdi:pencil" width={20} height={20} />
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => setOpenConfirm(true)}
            sx={{
              minWidth: '40px',
              height: '40px',
              borderRadius: '8px',
              borderColor: 'error.light',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
              },
            }}
          >
            <Icon icon="mdi:delete" width={20} height={20} />
          </Button>
        </Box>
      </Box>

      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ fontWeight: 'bold' }}>
          Delete Tagged Will
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the tagged Will for <strong>{tag.usertypeDetails?.userType}</strong>? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2, gap: 1 }}>
          <Button onClick={() => setOpenConfirm(false)} color="inherit" variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setOpenConfirm(false);
              handleDelete(tag);
            }}
            color="error"
            variant="contained"
            size="small"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const WillLanding = ({}) => {
  const [externalOpen, setExternalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { loading, taggedKra, lastTaggedKraType } = useSelector((state) => state.appraisal);

  const data = {
    type: 'will',
  };

  useEffect(() => {
    if (lastTaggedKraType !== data.type) {
      dispatch(getAssignedKra(data));
    }
  }, [lastTaggedKraType]);

  useEffect(() => {
    if (!externalOpen) {
      setEditData(null);
    }
  }, [externalOpen]);
  const handleDelete = (tag) => {
    dispatch(
      deleteAssignedByUserType({
        usertypeId: tag?.usertypeDetails?.userTypeId,
        type: 'will',
      }),
    ).then((res) => {
      if (res.payload.status === 'success') {
        enqueueSnackbar(res.payload.message, {
          variant: 'success',
        });
        dispatch(getAssignedKra(data));
      } else {
        enqueueSnackbar(res.payload.message, {
          variant: 'error',
        });
      }
    });
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          padding: 1,
        }}
      >
        <Box sx={{ width: '200px' }}></Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ alignItems: 'center' }} spacing={1}>
          <ImportData tagImport={true} library={'will'} dispatchData={data} />
          <Button size="small" variant="outlined" onClick={() => setExternalOpen(true)} sx={{ boxShadow: 'none' }}>
            Tag Will
          </Button>
          <Suspense fallback={<CustomSuspense text="Loading Tag Skill..." />}>
            {externalOpen && (
              <TagWill
                dispatchData={data}
                externalOpen={externalOpen}
                setExternalOpen={setExternalOpen}
                edit={editData ? true : false}
                editData={editData}
              />
            )}
          </Suspense>
          <Button
            size="small"
            variant="contained"
            sx={{ boxShadow: 'none' }}
            onClick={() => {
              const url = `${window.location.origin}/Appraisal-Management/operations-will-library`;
              window.open(url, '_blank');
            }}
          >
            Open Will Library
          </Button>
        </Stack>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Loader3D />
        </Box>
      ) : taggedKra.length === 0 ? (
        <Box
          sx={{
            width: '100%',
            padding: 2,
            boxShadow: ' rgba(0, 0, 0, 0.24) 0px 3px 8px;',
            borderRadius: 2,
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <img src={emptyKra} alt="Empty KRA" style={{ width: '20%', height: 'auto', objectFit: 'contain' }} />
          <Typography variant="h6" color="GrayText">
            No Will have been tagged
          </Typography>
        </Box>
      ) : (
        <Box>
          {taggedKra.map((tag, index) => (
            <WillCard
              key={tag._id || tag.usertypeDetails?.userTypeId || index}
              tag={tag}
              handleDelete={handleDelete}
              setEditData={setEditData}
              setExternalOpen={setExternalOpen}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default WillLanding;
